import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminManagers from './pages/admin/Managers';
import AdminSettings from './pages/admin/Settings';
import SuperAdminSettings from './pages/admin/SuperAdminSettings';
import AdminMeals from './pages/admin/Meals';
import AdminOrganizations from './pages/admin/Organizations';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import GuestReservations from './pages/admin/GuestReservations';
import GuestTokenPrint from './pages/admin/GuestTokenPrint';
import DeliveryGroups from './pages/admin/DeliveryGroups';
import ManagerMeals from './pages/manager/Meals';

import ManagerMenu from './pages/manager/Menu';
import ManagerUsers from './pages/manager/Users';
import DailyReservations from './pages/manager/DailyReservations';
import DailyMealPlanning from './pages/DailyMealPlanning';
import RestaurantMonitor from './pages/RestaurantMonitor';
import MonthlyReports from './pages/admin/MonthlyReports';
import UserReport from './pages/admin/UserReport';

const isSuperAdminDomain = window.location.hostname.startsWith('co.') || window.location.hostname.includes('superadmin');

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="text-center p-5">در حال بارگذاری...</div>;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  {isSuperAdminDomain ? <SuperAdminDashboard /> : <Dashboard />}
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/organizations"
            element={
              <PrivateRoute>
                <Layout>
                  <AdminOrganizations />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <PrivateRoute>
                <Layout>
                  <AdminUsers />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/users/:userId/reports"
            element={
              <PrivateRoute>
                <Layout>
                  <UserReport />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/delivery-groups"
            element={
              <PrivateRoute>
                <Layout>
                  <DeliveryGroups />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/print/guest-tokens"
            element={
              <PrivateRoute>
                <GuestTokenPrint />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/guest-reservations"
            element={
              <PrivateRoute>
                <Layout>
                  <GuestReservations />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/meals"
            element={
              <PrivateRoute>
                <Layout>
                  <AdminMeals />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <PrivateRoute>
                <Layout>
                  <MonthlyReports />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <PrivateRoute>
                <Layout>
                  {isSuperAdminDomain ? <SuperAdminSettings /> : <AdminSettings />}
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/manager/meals"
            element={
              <PrivateRoute>
                <Layout>
                  <ManagerMeals />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/manager/menu"
            element={
              <PrivateRoute>
                <Layout>
                  <ManagerMenu />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/manager/users"
            element={
              <PrivateRoute>
                <Layout>
                  <ManagerUsers />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/daily-planning"
            element={
              <PrivateRoute>
                <Layout>
                  <DailyMealPlanning />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/manager/daily-reservations"
            element={
              <PrivateRoute>
                <Layout>
                  <DailyReservations />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/monitor"
            element={
              <PrivateRoute>
                <RestaurantMonitor />
              </PrivateRoute>
            }
          />
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;
